'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    MessageSquare,
    Send,
    X,
    Minus,
    Maximize2,
    Bot,
    Sparkles,
    User,
    Loader2,
    Search,
    Home,
    Info,
    Trash2,
    Lock
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@/context/AuthContext';
import { API_BASE_URL } from '@/lib/api';
import { usePathname } from 'next/navigation';
import BalyMarketingAssistant from '@/app/dashboard/marketing/components/BalyMarketingAssistant';

const ChatAssistant = () => {
    const { user } = useAuth();
    const canUseBossMode = user?.planLimits?.services?.baly_assistant === true || user?.planLimits?.services?.baly_assistant_pro === true;
    const canUseBalyMarketing = user?.planLimits?.services?.baly_marketing === true;
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    // Configuración dinámica del asistente (identidad de la agencia y Modo Jefe)
    const [assistantConfig, setAssistantConfig] = useState(() => {
        if (typeof window !== 'undefined') {
            try {
                const cached = localStorage.getItem('ai_assistant_config');
                if (cached) return JSON.parse(cached);
            } catch (_) {}
        }
        return {
            agent_name: 'Baly',
            company_name: '',
            welcome_message: null
        };
    });

    const agentName = assistantConfig?.agent_name || 'Baly';
    const companyName = assistantConfig?.company_name || '';
    const userName = user?.first_name || user?.username || 'Jefe';

    const getDynamicWelcome = useCallback((name = agentName, comp = companyName, uName = userName) => {
        const compSuffix = comp ? ` de ${comp}` : '';
        return `¡Hola, ${uName}! Soy ${name}, tu asistente ejecutiva${compSuffix}. Estoy a tu disposición para revisar tu agenda y citas, consultar el inventario de propiedades, dar seguimiento a tus clientes o realizar cálculos financieros. ¿En qué te puedo apoyar hoy?`;
    }, [agentName, companyName, userName]);

    const [messages, setMessages] = useState(() => [
        {
            role: 'model',
            parts: [assistantConfig?.welcome_message || getDynamicWelcome()]
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const isNearBottomRef = useRef(true);

    const pathname = usePathname();
    const isMarketing = pathname?.includes('/dashboard/marketing');
    
    // Validar permisos según el módulo en el que estamos
    const isAllowed = isMarketing ? canUseBalyMarketing : canUseBossMode;
    const alertMessage = isMarketing 
        ? `${agentName} Marketing no está disponible en tu plan actual. Mejora a un plan superior para habilitar el agente de marketing.`
        : `${agentName} Modo Jefe (IA Avanzada) no está disponible en tu plan actual. Mejora tu suscripción para hablar con ${agentName}.`;

    // Cargar configuración de identidad del asistente de la agencia
    const loadAssistantConfig = useCallback(async () => {
        try {
            const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
            if (!token) return;
            const res = await fetch(`${API_BASE_URL}/assistant/config`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAssistantConfig(data);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('ai_assistant_config', JSON.stringify(data));
                }
                // Si la conversación aún no tiene mensajes de usuario, actualizar el mensaje inicial
                setMessages(prev => {
                    const hasUserMessage = prev.some(m => m.role === 'user');
                    if (!hasUserMessage) {
                        return [{
                            role: 'model',
                            parts: [data.welcome_message || getDynamicWelcome(data.agent_name, data.company_name, data.user_name)]
                        }];
                    }
                    return prev;
                });
            }
        } catch (err) {
            console.warn("No se pudo cargar la configuración del asistente:", err);
        }
    }, [getDynamicWelcome]);

    useEffect(() => {
        loadAssistantConfig();

        const handleIdentityUpdated = () => {
            loadAssistantConfig();
        };
        window.addEventListener('ai-agent-identity-updated', handleIdentityUpdated);
        return () => window.removeEventListener('ai-agent-identity-updated', handleIdentityUpdated);
    }, [loadAssistantConfig]);

    const sendMessage = useCallback(async (textToSend) => {
        if (!textToSend || !textToSend.trim() || isLoading) return;

        const userMessage = textToSend.trim();
        setInput('');
        setIsLoading(true);

        // Add user message to UI
        isNearBottomRef.current = true;
        setMessages(prev => [...prev, { role: 'user', parts: [userMessage] }]);

        try {
            const token = localStorage.getItem('token');
            const toleranceStr = localStorage.getItem('ai_tolerance') || "50";
            const toleranceFloat = parseInt(toleranceStr, 10) / 100.0;
            const response = await fetch(`${API_BASE_URL}/assistant/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: userMessage,
                    history: messages.slice(-10),
                    ai_tolerance: toleranceFloat
                })
            });

            if (!response.ok) {
                let errDetail = 'Error en el asistente';
                try {
                    const errData = await response.json();
                    if (errData?.detail) {
                        errDetail = typeof errData.detail === 'string' ? errData.detail : JSON.stringify(errData.detail);
                    }
                } catch (_) {}
                throw new Error(errDetail);
            }

            const data = await response.json();

            if (!data || data.response === undefined || data.response === null) {
                console.warn("Invalid response from server:", data);
                throw new Error("Respuesta inválida del servidor");
            }

            // Add assistant response to UI (with images if any)
            setMessages(prev => [...prev, {
                role: 'model',
                parts: [data.response || '...'],
                images: Array.isArray(data.images) ? data.images : []
            }]);
        } catch (error) {
            console.warn('Error assistant:', error.message);
            setMessages(prev => [...prev, {
                role: 'model',
                parts: [error.message || `Lo siento, hubo un problema al conectar con ${agentName}. Por favor, intenta de nuevo.`]
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, messages, agentName]);

    // Escuchar eventos globales (Topbar, Documentos y Expedientes)
    useEffect(() => {
        const handleToggle = () => setIsOpen(prev => !prev);
        const handleOpen = () => {
            setIsOpen(true);
            setIsMinimized(false);
        };
        const handleOpenWithPrompt = (event) => {
            const prompt = event.detail?.prompt;
            setIsOpen(true);
            setIsMinimized(false);
            if (prompt) {
                sendMessage(prompt);
            }
        };

        window.addEventListener('toggle-baly-assistant', handleToggle);
        window.addEventListener('open-baly-assistant', handleOpen);
        window.addEventListener('open-baly-with-prompt', handleOpenWithPrompt);

        return () => {
            window.removeEventListener('toggle-baly-assistant', handleToggle);
            window.removeEventListener('open-baly-assistant', handleOpen);
            window.removeEventListener('open-baly-with-prompt', handleOpenWithPrompt);
        };
    }, [sendMessage]);

    useEffect(() => {
        if (isOpen && !isMarketing) {
            // Cargar historial al abrir por primera vez (Solo para Boss Mode)
            const fetchHistory = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/assistant/history`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data.length > 0) {
                            // Convertir formato de DB a formato de UI
                            const historyMessages = data.map(m => ({
                                role: m.direction === 'received' ? 'user' : 'model',
                                parts: [m.text]
                            }));
                            setMessages(historyMessages);
                        } else {
                            // Sin historial previo: asegurar saludo dinámico en Modo Jefe
                            setMessages([{
                                role: 'model',
                                parts: [assistantConfig?.welcome_message || getDynamicWelcome()]
                            }]);
                        }
                    }
                } catch (err) {
                    console.error("Error fetching history:", err);
                }
            };
            fetchHistory();
        }
    }, [isOpen, isMarketing, assistantConfig?.welcome_message, getDynamicWelcome]);

    const handleScroll = useCallback(() => {
        const el = chatContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        isNearBottomRef.current = distanceFromBottom <= 100;
    }, []);

    const scrollToBottom = useCallback((force = false) => {
        if (force || isNearBottomRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            isNearBottomRef.current = true;
        }
    }, []);

    useEffect(() => {
        scrollToBottom(false);
    }, [messages, scrollToBottom]);

    const handleSend = async (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    const handleClearHistory = async () => {
        if (!window.confirm('¿Estás seguro de que deseas borrar todo el historial de chat?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/assistant/history`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setMessages([
                    {
                        role: 'model',
                        parts: [`Historial borrado. ¿En qué más puedo apoyarte hoy, ${userName}?`]
                    }
                ]);
            }
        } catch (err) {
            console.error("Error clearing history:", err);
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => {
                    if (!isAllowed) {
                        alert(alertMessage);
                        return;
                    }
                    setIsOpen(true);
                }}
                title={`${agentName} AI Copilot`}
                aria-label={`Abrir asistente ${agentName} IA`}
                className={`fixed bottom-6 right-6 text-white p-3.5 rounded-full shadow-xl transition-all z-50 flex items-center justify-center cursor-pointer ${
                    !isAllowed 
                        ? 'bg-slate-400 hover:bg-slate-500 cursor-not-allowed' 
                        : 'bg-[#714B67] hover:bg-[#5a3b52] hover:scale-105 shadow-md shadow-[#714B67]/30 ring-2 ring-white/30'
                }`}
            >
                {!isAllowed ? (
                    <Lock size={24} />
                ) : (
                    <div className="relative flex items-center justify-center">
                        <Sparkles size={24} className="text-white" />
                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
                    </div>
                )}
            </button>
        );
    }

    if (isMarketing) {
        return (
            <BalyMarketingAssistant 
                onClose={() => setIsOpen(false)} 
                isMinimized={isMinimized} 
                setIsMinimized={setIsMinimized} 
            />
        );
    }

    return (
        <div className={`fixed bottom-6 right-6 w-[768px] max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 flex flex-col transition-all overflow-hidden ${isMinimized ? 'h-14' : 'h-[600px]'}`}>
            {/* Header Estilo Odoo 20 */}
            <div className="bg-[#714B67] p-3 flex items-center justify-between text-white cursor-pointer select-none" onClick={() => isMinimized && setIsMinimized(false)}>
                <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-purple-200" />
                    <div>
                        <span className="font-semibold text-sm">{agentName} AI Copilot</span>
                        <span className="ml-2 text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full font-medium">Modo Jefe</span>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {!isMinimized && (
                        <button
                            onClick={(e) => { e.stopPropagation(); handleClearHistory(); }}
                            className="hover:bg-white/10 p-1.5 rounded-lg transition-colors mr-1 cursor-pointer"
                            title="Borrar historial"
                        >
                            <Trash2 size={15} />
                        </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }} className="hover:bg-white/10 p-1.5 rounded-lg transition-colors cursor-pointer">
                        {isMinimized ? <Maximize2 size={15} /> : <Minus size={15} />}
                    </button>
                    <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-1.5 rounded-lg transition-colors cursor-pointer">
                        <X size={15} />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <>
                    {/* Messages Area */}
                    <div 
                        ref={chatContainerRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50"
                    >
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-xs ${msg.role === 'user' ? 'bg-[#714B67]/15 text-[#714B67]' : 'bg-white text-[#714B67] border border-slate-200'}`}>
                                        {msg.role === 'user' ? <User size={15} /> : <Sparkles size={15} />}
                                    </div>
                                    <div className={`p-3.5 rounded-2xl text-[14px] leading-relaxed shadow-xs ${msg.role === 'user' ? 'bg-[#714B67] text-white rounded-tr-none' : 'bg-white text-slate-800 rounded-tl-none border border-slate-200'}`}>
                                        <div className="markdown-content text-inherit">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                                                    ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                                    ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                                                    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                                    strong: ({ node, ...props }) => <strong className="font-extrabold text-inherit" {...props} />,
                                                    a: ({ node, ...props }) => <a className="text-blue-600 underline hover:text-blue-800 font-bold" target="_blank" rel="noopener noreferrer" {...props} />,
                                                    table: ({ node, ...props }) => (
                                                        <div className="overflow-x-auto my-3 rounded-xl border border-gray-200 shadow-xs bg-white">
                                                            <table className="w-full text-xs text-left border-collapse" {...props} />
                                                        </div>
                                                    ),
                                                    thead: ({ node, ...props }) => <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[10px] tracking-wider border-b border-gray-200" {...props} />,
                                                    tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-100" {...props} />,
                                                    tr: ({ node, ...props }) => <tr className="hover:bg-slate-50/80 transition-colors" {...props} />,
                                                    th: ({ node, ...props }) => <th className="px-3 py-2 font-bold whitespace-nowrap text-slate-800" {...props} />,
                                                    td: ({ node, ...props }) => <td className="px-3 py-2 whitespace-nowrap text-slate-700" {...props} />,
                                                }}
                                            >
                                                {msg.parts[0]}
                                            </ReactMarkdown>
                                        </div>

                                        {/* Galería de imágenes si existen */}
                                        {msg.images && msg.images.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {msg.images.map((img, idx) => (
                                                    <img
                                                        key={idx}
                                                        src={`${API_BASE_URL}/images/${img}`}
                                                        alt="Propiedad"
                                                        className="w-32 h-24 object-cover rounded-lg border-2 border-white shadow-sm hover:scale-105 transition-transform cursor-pointer"
                                                        onClick={() => setSelectedImage(`${API_BASE_URL}/images/${img}`)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-gray-100 p-3 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin text-blue-600" />
                                    <span className="text-xs text-gray-500 italic">Consultando con {agentName}...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Actions para Modo Jefe */}
                    <div className="px-4 py-2 bg-slate-50 flex gap-2 overflow-x-auto border-t border-slate-200/80">
                        <button
                            onClick={() => setInput('¿Tengo citas agendadas para hoy o mañana?')}
                            className="text-[10px] font-medium bg-white border border-slate-200 px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-purple-50 hover:text-[#714B67] hover:border-purple-200 transition-colors cursor-pointer"
                        >
                            📅 Mis Citas Hoy/Mañana
                        </button>
                        <button
                            onClick={() => setInput('¿Qué propiedades disponibles tenemos en inventario?')}
                            className="text-[10px] font-medium bg-white border border-slate-200 px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-purple-50 hover:text-[#714B67] hover:border-purple-200 transition-colors cursor-pointer"
                        >
                            🏠 Consultar Inventario
                        </button>
                        <button
                            onClick={() => setInput('Dame un resumen de mis leads y clientes recientes')}
                            className="text-[10px] font-medium bg-white border border-slate-200 px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-purple-50 hover:text-[#714B67] hover:border-purple-200 transition-colors cursor-pointer"
                        >
                            👥 Leads Recientes
                        </button>
                        <button
                            onClick={() => setInput('Calcula financiamiento para cuota de enganche')}
                            className="text-[10px] font-medium bg-white border border-slate-200 px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-purple-50 hover:text-[#714B67] hover:border-purple-200 transition-colors cursor-pointer"
                        >
                            💰 Calcular Financiamiento
                        </button>
                    </div>

                    {/* Input Area */}
                    <form onSubmit={handleSend} className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={`Pregúntale a ${agentName} sobre tu agenda, inventario, clientes...`}
                            className="flex-1 bg-slate-100 rounded-xl px-4 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#714B67] focus:bg-white transition-all"
                        />
                        <button
                            type="submit"
                            disabled={isLoading || !input.trim()}
                            className="bg-[#714B67] hover:bg-[#5a3b52] text-white p-2 rounded-xl transition-colors disabled:opacity-40 cursor-pointer"
                        >
                            <Send size={18} />
                        </button>
                    </form>
                </>
            )}

            {/* Modal de Zoom de Imagen */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all border border-white/20 z-[110]"
                        onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
                    >
                        <X size={32} strokeWidth={3} />
                    </button>

                    <img
                        src={selectedImage}
                        alt="Zoom"
                        className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl border-4 border-white/10 animate-in zoom-in-95 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    />

                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 text-xs font-medium tracking-widest uppercase">
                        Haz clic afuera para cerrar
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatAssistant;
